import { useState, useEffect, useCallback } from "react";

import {
  getDashboardStatus,
  trackRecord,
  type DashboardStatusSummary,
  type TrackRecordResult,
} from "api/dashboard";

interface UseDashboardHomeReturn {
  stats: DashboardStatusSummary | null;
  statsLoading: boolean;
  statsError: string | null;
  trackResult: TrackRecordResult | null;
  trackLoading: boolean;
  trackError: string | null;
  handleTrackRecord: (query: string) => Promise<TrackRecordResult | null>;
  refetchStats: () => Promise<void>;
}

export interface UseDashboardHomeOptions {
  /** Pre-populate track results (e.g. when entering from router state). */
  initialTrackResult?: TrackRecordResult | null;
}

export const useDashboardHome = (
  options?: UseDashboardHomeOptions
): UseDashboardHomeReturn => {
  const [stats, setStats] = useState<DashboardStatusSummary | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  const [trackResult, setTrackResult] = useState<TrackRecordResult | null>(
    () => options?.initialTrackResult ?? null
  );
  const [trackLoading, setTrackLoading] = useState<boolean>(false);
  const [trackError, setTrackError] = useState<string | null>(null);

  const fetchStats = useCallback(async (): Promise<void> => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const data = await getDashboardStatus();
      setStats(data);
    } catch (error) {
      setStatsError(
        error instanceof Error ? error.message : "Failed to load dashboard stats"
      );
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  const handleTrackRecord = useCallback(async (query: string): Promise<TrackRecordResult | null> => {
    const trimmed = query.trim();
    if (!trimmed) return null;

    setTrackLoading(true);
    setTrackError(null);
    setTrackResult(null);

    try {
      const isNumeric = /^\d+$/.test(trimmed);
      const result = await trackRecord(
        isNumeric ? { tmNo: trimmed } : { mark: trimmed }
      );
      setTrackResult(result);
      return result;
    } catch (error) {
      setTrackError(
        error instanceof Error ? error.message : "Failed to track record"
      );
      return null;
    } finally {
      setTrackLoading(false);
    }
  }, []);

  return {
    stats,
    statsLoading,
    statsError,
    trackResult,
    trackLoading,
    trackError,
    handleTrackRecord,
    refetchStats: fetchStats,
  };
};
